import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Route, useLocation } from "react-router";

import { IconMagnifyingGlass } from "@btnet/blueprint-icons";
import { BTText } from "@btnet/core-components";
import { FeedbackTarget } from "@btnet/services-btnet/handlers/models/feedbackTarget";
import { cn } from "@btnet/utilities-core";

import { type BuilderInfo } from "helpers/AppProvider.types";
import { AppDefaultInfoContext } from "helpers/globalContext/AppDefaultInfoContext";
import { EnvironmentInfoContext } from "helpers/globalContext/EnvironmentInfoContext";

import { useTracking } from "utilities/analytics/analytics";
import { routes } from "utilities/routes";

import { BTButton } from "commonComponents/btWrappers/BTButton/BTButton";
import { BTIconLoadingOutlined, BTIconMagnifyingGlassX } from "commonComponents/btWrappers/BTIcon";
import FeedbackWidget from "commonComponents/feedback/FeedbackWidget/FeedbackWidget";
import { SearchBarRoutes } from "commonComponents/utilities/MainNavigation/searchLegacy/SearchBarRoutes";

import "./AiSearch.less";

import { AiSearchAnnouncer } from "./AiSearchAnnouncer";
import { AiSearchQASlot } from "./AiSearchQASlot";
import { AiSearchResults, isVisibleHit } from "./AiSearchResults";
import { legacyVisibility, stableListVisibility } from "./aiSearchVisibility";
import { DevContextSwitcher } from "./DevContextSwitcher";
import { useAiSearch } from "./hooks/useAiSearch";
import { useAiSearchKeyboard } from "./hooks/useAiSearchKeyboard";
import { useAiSearchNavigation } from "./hooks/useAiSearchNavigation";
import { useAiSearchTelemetry } from "./hooks/useAiSearchTelemetry";
import { useAuthToken } from "./hooks/useAuthToken";
import { useSearchSuggest } from "./hooks/useSearchSuggest";

const DEBOUNCE_MS = 300;
const MIN_QUERY_LENGTH = 2;
const SKELETON_COUNT = 4;
/**
 * PR-A2: progress width floor. Before intent lands (no honest denominator —
 * see the progress block below) and for the 0-of-N instant after plans are
 * announced, the bar shows this sliver as the activity cue that replaced the
 * fake 1s loop animation.
 */
const PROGRESS_FLOOR_PCT = 5;

interface IAiSearchProps {
    handleHideSearchInput?: () => void;
    onFocus?: () => void;
    isSmallWidth: boolean;
    /**
     * Owned by the caller because `commonComponents` may not read builder flags
     * directly. Pass `useContext(BuilderInfoContext)` from the parent.
     */
    builderInfo?: BuilderInfo | null;
}

function SkeletonRows() {
    return (
        <div className="ai-search-skeleton-list" data-testid="ai-search-skeleton-list">
            {Array.from({ length: SKELETON_COUNT }, (_, i) => (
                <div className="ai-search-skeleton" key={i}>
                    <div
                        className="ai-search-skeleton-icon"
                        data-testid="ai-search-skeleton-icon"
                    />
                    <div className="ai-search-skeleton-lines">
                        <div
                            className="ai-search-skeleton-line ai-search-skeleton-line--medium"
                            data-testid="ai-search-skeleton-line"
                        />
                        <div
                            className="ai-search-skeleton-line ai-search-skeleton-line--short"
                            data-testid="ai-search-skeleton-line"
                        />
                    </div>
                </div>
            ))}
        </div>
    );
}

export function AiSearch(props: IAiSearchProps) {
    const { handleHideSearchInput, onFocus, isSmallWidth, builderInfo } = props;
    const { doSearch, cancel, state, stableListEnabled } = useAiSearch();
    const { trackEvent } = useTracking({ component: "AI Search" });
    const location = useLocation();
    const appDefaultInfo = useContext(AppDefaultInfoContext);
    const environmentInfo = useContext(EnvironmentInfoContext);
    const suggestBaseUrl = appDefaultInfo?.searchServiceConfiguration?.apiBaseUrl ?? "";

    const [query, setQuery] = useState("");
    // graft/A5: typeahead + did-you-mean (dev-surface gated; inert when the
    // suggest endpoint is absent — i.e. every deployed environment today).
    const suggestAudience = appDefaultInfo?.searchServiceConfiguration?.authAudience ?? "";
    const { getToken: getSuggestToken } = useAuthToken(suggestAudience);
    const {
        suggestions,
        corrected,
        reset: resetSuggest,
        enabled: suggestEnabled,
    } = useSearchSuggest(suggestBaseUrl, query, getSuggestToken);
    const [panelOpen, setPanelOpen] = useState(isSmallWidth);
    // PR-A2: user dismissed the per-op-error footer note for this search.
    const [opErrorsDismissed, setOpErrorsDismissed] = useState(false);
    const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const resultsRef = useRef<HTMLDivElement>(null);
    const panelRef = useRef<HTMLDivElement>(null);
    const [dropdownLeft, setDropdownLeft] = useState<number | undefined>(undefined);

    const results = useMemo(() => state.results.filter(isVisibleHit), [state.results]);
    const staleResults = useMemo(
        () => state.staleResults.filter(isVisibleHit),
        [state.staleResults],
    );
    // PR-A1 (stable list): late-op hits rendered below the committed list.
    // Same view-level visibility filter as the committed rows.
    const appendedResults = useMemo(
        () => state.appendedResults.filter(isVisibleHit),
        [state.appendedResults],
    );
    // The FULL visible list, committed rows first then appended rows: keyboard
    // navigation walks it as one listbox, and telemetry reports it as the
    // ranking the user actually saw. Identical to `results` when the stable
    // list flag is off (appendedResults is always empty on the legacy path).
    const navigableResults = useMemo(
        () => (appendedResults.length > 0 ? [...results, ...appendedResults] : results),
        [results, appendedResults],
    );

    const hasQuery = query.trim().length >= MIN_QUERY_LENGTH;
    const hasResults = results.length > 0;
    const hasStaleResults = staleResults.length > 0;
    const showPanel = panelOpen;
    const showInstruction = panelOpen && !hasQuery && state.phase === "idle";
    // resultCount spans both sections: keyboard nav walks committed rows then
    // appended rows, and telemetry reports the full visible count.
    const resultCount = results.length + appendedResults.length;
    // Each rendering contract's visibility rules live in their own pure
    // function (aiSearchVisibility.ts) — the flag is fixed per mount, so the
    // contracts never mix within a render (review 1511770).
    const { showSkeleton, showStaleResults, showResults, showAppended, showEmpty } =
        stableListEnabled
            ? stableListVisibility({
                  hasQuery,
                  phase: state.phase,
                  committed: state.committed,
                  resultCount,
                  hasResults,
                  appendedCount: appendedResults.length,
              })
            : legacyVisibility({
                  hasQuery,
                  phase: state.phase,
                  hasResults,
                  hasStaleResults,
              });

    // graft/A6: the `Search` event family and the abandonment ledger. Owns the
    // outcome snapshot and the phase→event effect that used to live here.
    const { trackSearch, abandonCurrent, markClicked } = useAiSearchTelemetry({
        state,
        resultCount,
        navigableResults,
        trackEvent,
    });

    // graft/A6: highlighted-row state, key-based relocation across
    // commits/appends, and scroll-into-view.
    const { highlightedIndex, moveUp, moveDown, clearHighlight, activeDescendant } =
        useAiSearchKeyboard({
            navigableResults,
            searchId: state.searchId,
            committed: state.committed,
        });

    // graft/A6: `LinkClick` + the click-join payload + entity navigation. The
    // post-click dismissal is the panel's business, so it arrives through
    // `onResultActivated` — read from a ref so handleResultClick's identity
    // doesn't churn when handleClose is recreated.
    const scheduleCloseRef = useRef<() => void>(() => {});
    const onResultActivated = useCallback(() => scheduleCloseRef.current(), []);
    const { pendingKey, clearPendingKey, handleResultClick, navigateToIndex } =
        useAiSearchNavigation({
            state,
            query,
            navigableResults,
            staleResults,
            resultCount,
            builderInfo,
            trackEvent,
            trackSearch,
            markClicked,
            onResultActivated,
        });

    const handleInputChange = useCallback(
        (value: string) => {
            setQuery(value);
            setPanelOpen(true);
            clearHighlight();
            // Cancel any pending post-click close — otherwise the 150ms timer
            // from handleResultClick wipes the user's newly-typed query.
            clearPendingKey();
            if (closeTimeoutRef.current) {
                clearTimeout(closeTimeoutRef.current);
                closeTimeoutRef.current = null;
            }

            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }

            if (value.trim().length < MIN_QUERY_LENGTH) {
                // Clearing/shortening below the threshold abandons the current
                // completed search just like a close or navigation — emit before
                // cancel() flips phase to "idle" and disqualifies the snapshot.
                // abandonCurrent's done/empty guard ignores in-flight cancellations
                // and the dedup set prevents a double-emit if a close/nav follows.
                abandonCurrent();
                cancel();
                return;
            }

            debounceRef.current = setTimeout(() => {
                doSearch(value.trim());
            }, DEBOUNCE_MS);
        },
        [doSearch, cancel, abandonCurrent, clearHighlight, clearPendingKey],
    );

    const handleClose = useCallback(() => {
        abandonCurrent();
        if (closeTimeoutRef.current) {
            clearTimeout(closeTimeoutRef.current);
            closeTimeoutRef.current = null;
        }
        setPanelOpen(false);
        setQuery("");
        clearHighlight();
        clearPendingKey();
        cancel();
        handleHideSearchInput?.();
    }, [cancel, handleHideSearchInput, abandonCurrent, clearHighlight, clearPendingKey]);

    // Post-click dismissal, handed to useAiSearchNavigation through a ref: a
    // brief acknowledgement on the clicked row, then close. Panel-only — the
    // routed page has nothing to dismiss.
    useEffect(() => {
        scheduleCloseRef.current = () => {
            if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
            closeTimeoutRef.current = setTimeout(() => {
                closeTimeoutRef.current = null;
                handleClose();
            }, 150);
        };
    }, [handleClose]);

    // Clear query/panel state on route navigation so input content doesn't
    // leak forward across back/forward or sidebar nav. Skip our own /SearchResult/
    // pushes (triggered by handleResultClick → navigateEntityLink).
    const previousPathnameRef = useRef(location.pathname);
    useEffect(() => {
        if (location.pathname === previousPathnameRef.current) return;
        const isSelfPushToModal = location.pathname.startsWith(
            previousPathnameRef.current.replace(/\/$/, "") + routes.search.resultModalPrefix + "/",
        );
        previousPathnameRef.current = location.pathname;
        if (isSelfPushToModal) return;
        // Navigating elsewhere (sidebar, back/forward) without clicking a result
        // is an abandonment of the current search.
        abandonCurrent();
        if (closeTimeoutRef.current) {
            clearTimeout(closeTimeoutRef.current);
            closeTimeoutRef.current = null;
        }
        setQuery("");
        clearHighlight();
        clearPendingKey();
        setPanelOpen(isSmallWidth);
        cancel();
    }, [location.pathname, isSmallWidth, cancel, abandonCurrent, clearHighlight, clearPendingKey]);

    const handleFocus = useCallback(() => {
        setPanelOpen(true);
        onFocus?.();
    }, [onFocus]);

    // Focus input when panel opens at small widths (input transitions from hidden to visible)
    useEffect(() => {
        if (panelOpen && isSmallWidth && inputRef.current) {
            inputRef.current.focus();
        }
    }, [panelOpen, isSmallWidth]);

    // Left-align dropdown with input at wide widths, updating on resize
    useEffect(() => {
        if (!panelOpen || isSmallWidth || !inputRef.current) {
            setDropdownLeft(undefined);
            return;
        }

        const input = inputRef.current;
        const sync = () => setDropdownLeft(input.getBoundingClientRect().left);
        sync();

        const ro = new ResizeObserver(sync);
        ro.observe(document.body);
        return () => ro.disconnect();
    }, [panelOpen, isSmallWidth]);

    // Keyboard navigation. The highlight itself (index, key-based relocation,
    // wrap bounds) lives in useAiSearchKeyboard; this switch only maps keys to
    // it, plus the panel-specific Escape.
    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            const count = navigableResults.length;

            switch (e.key) {
                case "ArrowDown": {
                    e.preventDefault();
                    if (count === 0) return;
                    moveDown();
                    break;
                }
                case "ArrowUp": {
                    e.preventDefault();
                    if (count === 0) return;
                    moveUp();
                    break;
                }
                case "Enter": {
                    if (highlightedIndex >= 0 && highlightedIndex < count) {
                        e.preventDefault();
                        navigateToIndex(highlightedIndex);
                    }
                    break;
                }
                case "Escape": {
                    e.preventDefault();
                    handleClose();
                    break;
                }
            }
        },
        [navigableResults.length, highlightedIndex, moveDown, moveUp, navigateToIndex, handleClose],
    );

    // Close on outside click
    useEffect(() => {
        if (!panelOpen) return;

        const handleClickOutside = (e: MouseEvent) => {
            if (isFeedbackOpen) return;
            const target = e.target as Node;
            if (
                panelRef.current &&
                !panelRef.current.contains(target) &&
                inputRef.current &&
                !inputRef.current.contains(target)
            ) {
                handleClose();
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [panelOpen, handleClose, isFeedbackOpen]);

    // Abort in-flight requests and clear timers on unmount ONLY. The final
    // abandonment event is emitted by useAiSearchTelemetry's own unmount
    // cleanup, which runs first (hooks clean up in declaration order), so the
    // ordering the pre-A6 single effect guaranteed is preserved. cancel is
    // stable (useCallback with empty deps in the hook).
    useEffect(() => {
        return () => {
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }
            if (closeTimeoutRef.current) {
                clearTimeout(closeTimeoutRef.current);
            }
            cancel();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const resultCountText = resultCount === 1 ? "1 result" : `${resultCount} results`;

    // ── PR-A2: real progress from the per-op bookkeeping ─────────────────
    // settled/total over state.operations replaces the fake 1s loop
    // animation. "Settled" includes errored ops: a failed op is finished
    // work, and a bar that waits on it would never reach its end.
    //
    // A2-review (thread 1511889): the bar is INDETERMINATE (floor width, no
    // aria values) until the intent event lands. Default ops are only
    // knowable from their own result events, so before intent every op
    // enters the array already settled — numerator and denominator increment
    // together and the bar pins to a dishonest 100%, then jumps BACKWARDS
    // when intent announces plans. intent is the first moment an honest
    // denominator exists; after it, a newly discovered op arrives settled,
    // so the fraction is monotonic non-decreasing.
    const opsTotal = state.operations.length;
    const opsSettled = state.operations.filter(
        (op) => op.status === "complete" || op.status === "error",
    ).length;
    const progressDeterminate = state.intentReceived && opsTotal > 0;
    const progressPct = progressDeterminate
        ? Math.max(PROGRESS_FLOOR_PCT, Math.round((opsSettled / opsTotal) * 100))
        : PROGRESS_FLOOR_PCT;

    // PR-A2: per-search reset of the footer-note dismissal. The QA expansion
    // and the live region reset with their own components (AiSearchQASlot,
    // AiSearchAnnouncer), both keyed on the same searchId.
    useEffect(() => {
        setOpErrorsDismissed(false);
    }, [state.searchId]);
    const showOpErrorsNote =
        hasQuery &&
        !state.error &&
        state.opErrors.length > 0 &&
        !opErrorsDismissed &&
        state.phase !== "idle";

    const feedbackExtraInfo = JSON.stringify({
        searchId: state.searchId,
        query,
        resultCount,
        topResults: navigableResults.slice(0, 10).map((r) => ({
            entity: r.entity,
            id: r.id,
            summary: r.summary,
        })),
    });

    return (
        <div className="ai-search-wrapper">
            <div className="ai-search-input-row">
                {/* Native input required: BTInput doesn't support combobox
                    role, aria-activedescendant, or aria-expanded */}
                {/* eslint-disable-next-line react/forbid-elements */}
                <input
                    ref={inputRef}
                    className="ai-search-overlay-input"
                    type="text"
                    placeholder={isSmallWidth ? "Search..." : "Search or ask a question..."}
                    value={query}
                    onChange={(e) => handleInputChange(e.target.value)}
                    onFocus={handleFocus}
                    onKeyDown={handleKeyDown}
                    role="combobox"
                    aria-expanded={hasQuery}
                    aria-controls="ai-search-listbox"
                    aria-activedescendant={activeDescendant}
                    aria-autocomplete="list"
                    data-testid="ai-search-input"
                />
            </div>

            {showPanel && (
                <>
                    <div
                        className={cn("ai-search-backdrop", {
                            "ai-search-backdrop--behind-modal": isFeedbackOpen,
                        })}
                        onClick={() => {
                            if (!isFeedbackOpen) handleClose();
                        }}
                        data-testid="ai-search-overlay"
                    />
                    <div
                        ref={panelRef}
                        className={cn("ai-search-dropdown", {
                            "ai-search-dropdown--mobile": isSmallWidth,
                            "ai-search-dropdown--behind-modal": isFeedbackOpen,
                        })}
                        style={
                            !isSmallWidth && dropdownLeft !== undefined
                                ? { left: dropdownLeft }
                                : undefined
                        }
                        data-testid="ai-search-panel"
                    >
                        {/* graft/A5: dev-only builder/user switcher (probe-gated) */}
                        <DevContextSwitcher
                            searchBaseUrl={suggestBaseUrl}
                            isProduction={environmentInfo?.isProduction ?? true}
                            onChanged={() => {
                                if (query.trim()) doSearch(query.trim());
                            }}
                        />

                        {/* PR-A2: progress bar driven by the real per-op
                            bookkeeping (settled/total operations) instead of
                            the fake 1s loop animation; indeterminate until
                            intent provides an honest denominator. */}
                        {(state.phase === "loading" || state.phase === "streaming") && (
                            <div
                                className="ai-search-progress"
                                role="progressbar"
                                aria-label="Search progress"
                                aria-valuemin={0}
                                aria-valuemax={progressDeterminate ? opsTotal : undefined}
                                aria-valuenow={progressDeterminate ? opsSettled : undefined}
                                data-testid="ai-search-progress"
                            >
                                <div
                                    className="ai-search-progress-bar ai-search-progress-bar--ops"
                                    style={{ width: `${progressPct}%` }}
                                    data-testid="ai-search-progress-bar"
                                />
                            </div>
                        )}

                        {/* Instruction: no query yet */}
                        {showInstruction && (
                            <div
                                className="ai-search-instruction"
                                data-testid="ai-search-instruction"
                            >
                                <div className="ai-search-instruction-icon">
                                    <IconMagnifyingGlass />
                                </div>
                                <h3>Find anything in Buildertrend</h3>
                                <p>Search across all your jobs.</p>
                            </div>
                        )}

                        {/* Skeleton loading (first search only, no prior results) */}
                        {showSkeleton && <SkeletonRows />}

                        {/* Stale results — faded previous results while new search loads */}
                        {showStaleResults && (
                            <div
                                className="ai-search-results-scroll ai-search-results-scroll--stale"
                                data-testid="ai-search-stale-results"
                            >
                                <AiSearchResults
                                    results={staleResults}
                                    query={query}
                                    highlightedIndex={-1}
                                    pendingKey={pendingKey}
                                    onResultClick={handleResultClick}
                                    builderInfo={builderInfo}
                                />
                            </div>
                        )}

                        {/* Fatal error banner (auth/transport/all-ops-failed).
                            PR-A2: per-op errors no longer render here — they
                            demote to the dismissible footer note below. */}
                        {state.error && (
                            <div className="ai-search-error" data-testid="ai-search-error">
                                {state.error}
                            </div>
                        )}

                        {/* graft/A5: did-you-mean (spell banner) */}
                        {corrected && (
                            <div
                                className="ai-search-dym"
                                data-testid="ai-search-dym"
                                style={{
                                    padding: "6px 16px",
                                    fontSize: 13,
                                    background: "#fff8e6",
                                    borderBottom: "1px solid #f0e2b6",
                                }}
                            >
                                Did you mean{" "}
                                <button
                                    type="button"
                                    data-testid="ai-search-dym-apply"
                                    onClick={() => {
                                        setQuery(corrected);
                                        resetSuggest();
                                        doSearch(corrected);
                                    }}
                                    style={{
                                        border: "none",
                                        background: "none",
                                        color: "#8a6d1a",
                                        fontWeight: 700,
                                        textDecoration: "underline",
                                        cursor: "pointer",
                                        padding: 0,
                                        fontSize: 13,
                                    }}
                                >
                                    {corrected}
                                </button>
                                ?
                            </div>
                        )}

                        {/* graft/A5: typeahead suggestions (authed suggest route) */}
                        {/* graft/A5: v3 AgentTriageBanner — invite the agent
                            once a query has committed. Quiet by default,
                            nudges on question-shaped queries. Probe-gated:
                            suggestEnabled goes false wherever the authed
                            suggest route is absent (404 or SPA-HTML), so
                            this renders nowhere the dock doesn't. */}
                        {suggestEnabled && state.searchQuery.trim().length > 0 && (
                            <div
                                data-testid="ai-search-agent-banner"
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 8,
                                    padding: "6px 16px",
                                    fontSize: 13,
                                    background:
                                        /^(who|what|where|when|why|how|which|is|are|does|do|can|should)/i.test(
                                            state.searchQuery,
                                        ) || state.searchQuery.includes("?")
                                            ? "#f3efff"
                                            : "#f7f9fb",
                                    borderBottom: "1px solid #e3e8ee",
                                    color: "#4a5560",
                                }}
                            >
                                <span
                                    style={{
                                        flex: 1,
                                        minWidth: 0,
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    Want more than a list? The search agent can dig in, total, and
                                    follow up.
                                </span>
                                <button
                                    type="button"
                                    data-testid="ai-search-agent-banner-open"
                                    onClick={() => {
                                        window.dispatchEvent(
                                            new CustomEvent("bt:openSearchAgent", {
                                                detail: { query: state.searchQuery },
                                            }),
                                        );
                                        handleClose();
                                    }}
                                    style={{
                                        flexShrink: 0,
                                        padding: "3px 10px",
                                        borderRadius: 5,
                                        border: "none",
                                        background: "#1f4f6f",
                                        color: "#fff",
                                        fontSize: 12,
                                        cursor: "pointer",
                                    }}
                                >
                                    Ask the agent →
                                </button>
                            </div>
                        )}

                        {suggestions.length > 0 && (
                            <div className="ai-search-suggest" data-testid="ai-search-suggest">
                                {suggestions.map((sg, i) => (
                                    <button
                                        key={`${sg.entity_type}-${sg.id}-${i}`}
                                        type="button"
                                        data-testid="ai-search-suggest-item"
                                        onClick={() => {
                                            setQuery(sg.text);
                                            resetSuggest();
                                            doSearch(sg.text);
                                        }}
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 8,
                                            width: "100%",
                                            textAlign: "left",
                                            padding: "5px 16px",
                                            border: "none",
                                            background: "none",
                                            cursor: "pointer",
                                            fontSize: 13,
                                            color: "#3c4650",
                                        }}
                                    >
                                        <span
                                            style={{
                                                color: "#8a94a0",
                                                fontSize: 11,
                                                textTransform: "uppercase",
                                                minWidth: 90,
                                            }}
                                        >
                                            {sg.entity_type.replace(/_/g, " ")}
                                        </span>
                                        <span
                                            style={{
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                whiteSpace: "nowrap",
                                            }}
                                        >
                                            {sg.text}
                                        </span>
                                    </button>
                                ))}
                            </div>
                        )}

                        {/* PR-A2: reserved-height QA slot — renders nothing
                            when no slot is owed. graft/A6: extracted so the
                            routed page reuses the layout contract. */}
                        <AiSearchQASlot
                            qaAnswers={state.qaAnswers}
                            phase={state.phase}
                            isQuestion={state.isQuestion}
                            hasQuery={hasQuery}
                            searchId={state.searchId}
                        />

                        {/* PR-A2: screen-reader-only result-count announcement.
                            Announces once per commit (flag-on) / once per paint
                            (flag-off) — the count header below is no longer a
                            live region, so per-event text churn stays silent. */}
                        <AiSearchAnnouncer
                            resultCount={resultCount}
                            stableListEnabled={stableListEnabled}
                            committed={state.committed}
                            searchId={state.searchId}
                        />

                        {/* Result count header. PR-A2: NOT a live region —
                            the sr-only announcement span above owns the
                            screen-reader count (once per commit/paint);
                            A3's testid kept for the click-telemetry suite. */}
                        {showResults && (
                            <div
                                className="ai-search-count-header"
                                data-testid="ai-search-count-header"
                            >
                                <span>
                                    {state.phase === "streaming" && (
                                        <BTIconLoadingOutlined
                                            spin
                                            size={12}
                                            className="ai-search-streaming-spinner"
                                        />
                                    )}
                                    {state.phase === "streaming"
                                        ? `${resultCountText} so far...`
                                        : resultCountText}
                                </span>
                                <BTButton
                                    type="tertiary"
                                    onClick={() => setIsFeedbackOpen(true)}
                                    data-testid="ai-search-feedback-link"
                                    className="ai-search-feedback-link"
                                >
                                    Not finding it? Tell us
                                </BTButton>
                            </div>
                        )}

                        {isFeedbackOpen && (
                            <FeedbackWidget
                                feedbackTarget={FeedbackTarget.AiSearch}
                                onClose={() => setIsFeedbackOpen(false)}
                                extraInfo={feedbackExtraInfo}
                            />
                        )}

                        {/* Scrollable results. PR-A1: late-op hits render BELOW the
                            committed rows inside the SAME listbox (an aria "group"
                            separator, not a second listbox) so aria-activedescendant
                            containment holds for appended rows — review 1511768. */}
                        {showResults && (
                            <div className="ai-search-results-scroll" ref={resultsRef}>
                                <AiSearchResults
                                    results={results}
                                    appendedResults={showAppended ? appendedResults : undefined}
                                    query={query}
                                    highlightedIndex={highlightedIndex}
                                    pendingKey={pendingKey}
                                    onResultClick={handleResultClick}
                                    builderInfo={builderInfo}
                                />
                            </div>
                        )}

                        {/* PR-A2: per-op errors demoted to a dismissible
                            footer note — some sources didn't respond, but the
                            results that did are on screen and undisturbed. */}
                        {showOpErrorsNote && (
                            <div className="ai-search-op-errors" data-testid="ai-search-op-errors">
                                <span className="ai-search-op-errors-text">
                                    Some sources didn&rsquo;t respond &mdash;{" "}
                                    {state.opErrors.join(" · ")}
                                </span>
                                <BTButton
                                    type="tertiary"
                                    onClick={() => setOpErrorsDismissed(true)}
                                    data-testid="ai-search-op-errors-dismiss"
                                    className="ai-search-op-errors-dismiss"
                                    aria-label="Dismiss source errors"
                                >
                                    Dismiss
                                </BTButton>
                            </div>
                        )}

                        {/* Empty state */}
                        {showEmpty && (
                            <div
                                className="ai-search-empty"
                                aria-live="polite"
                                data-testid="ai-search-empty"
                            >
                                <div className="ai-search-empty-icon" aria-hidden="true">
                                    <BTIconMagnifyingGlassX size={32} />
                                </div>
                                <BTText
                                    size="distinct-sm"
                                    tag="p"
                                    className="ai-search-empty-title"
                                >
                                    No results for &ldquo;{query}&rdquo;
                                </BTText>
                                <BTText size="normal-sm" tag="p" className="ai-search-empty-hint">
                                    Check your spelling or try broader terms.
                                </BTText>
                            </div>
                        )}

                        {/* Footer with keyboard hints */}
                        {showResults && (
                            <div className="ai-search-footer" data-testid="ai-search-footer">
                                <div className="ai-search-shortcuts">
                                    <span className="ai-search-shortcut-group">
                                        <kbd>&uarr;</kbd>
                                        <kbd>&darr;</kbd>
                                        navigate
                                    </span>
                                    <span className="ai-search-shortcut-group">
                                        <kbd>&crarr;</kbd>
                                        select
                                    </span>
                                    <span className="ai-search-shortcut-group">
                                        <kbd>esc</kbd>
                                        close
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>
                </>
            )}
            {/* SearchBarRoutes must stay mounted after the panel closes so the
                pushed /SearchResult/* route can render the entity modal */}
            <Route render={(routeProps) => <SearchBarRoutes {...routeProps} />} />
        </div>
    );
}
